import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  ImageRun,
  Header,
  Footer,
  PageNumber,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
} from "docx";
import { htmlToBlocks, funderSlug } from "./htmlToBlocks";

interface ExportArgs {
  html: string;
  grantName: string;
  funder: string;
  ptaName: string;
  ptaLogoUrl?: string | null;
}

async function fetchLogoBytes(url: string): Promise<Uint8Array | null> {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

export async function exportGrantLetterDocx({ html, grantName, funder, ptaName, ptaLogoUrl }: ExportArgs) {
  const blocks = htmlToBlocks(html);
  const logoBytes = ptaLogoUrl ? await fetchLogoBytes(ptaLogoUrl) : null;

  // Header: 2-col table — logo left, PTA name + date right
  const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  const noBorders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };

  const headerLeftChildren = logoBytes
    ? [new Paragraph({ children: [new ImageRun({ type: "png", data: logoBytes, transformation: { width: 90, height: 60 } })] })]
    : [new Paragraph({ children: [new TextRun({ text: ptaName, bold: true })] })];

  const headerTable = new Table({
    width: { size: 9000, type: WidthType.DXA },
    columnWidths: [4500, 4500],
    rows: [
      new TableRow({
        children: [
          new TableCell({ borders: noBorders, width: { size: 4500, type: WidthType.DXA }, children: headerLeftChildren }),
          new TableCell({
            borders: noBorders,
            width: { size: 4500, type: WidthType.DXA },
            children: [
              new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: ptaName, bold: true, size: 24 })] }),
              new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: new Date().toLocaleDateString("en-GB"), color: "888888", size: 18 })] }),
            ],
          }),
        ],
      }),
    ],
  });

  const bodyParagraphs: (Paragraph | Table)[] = [
    headerTable,
    new Paragraph({ children: [new TextRun("")] }),
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      children: [new TextRun({ text: `Re: ${grantName} — ${funder}`, bold: true })],
    }),
    new Paragraph({ children: [new TextRun("")] }),
  ];

  for (const b of blocks) {
    if (b.type === "heading") {
      const level = b.level === 1 ? HeadingLevel.HEADING_1 : b.level === 2 ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_3;
      bodyParagraphs.push(
        new Paragraph({
          heading: level,
          children: b.runs.map((r) => new TextRun({ text: r.text, bold: r.bold, italics: r.italic })),
        }),
      );
    } else if (b.type === "paragraph") {
      bodyParagraphs.push(
        new Paragraph({
          spacing: { after: 120 },
          children: b.runs.map((r) => new TextRun({ text: r.text, bold: r.bold, italics: r.italic })),
        }),
      );
    } else if (b.type === "list") {
      b.items.forEach((item, idx) => {
        const text = item.map((r) => r.text).join("");
        bodyParagraphs.push(
          new Paragraph({
            children: [new TextRun({ text: `${b.ordered ? `${idx + 1}. ` : "• "}${text}` })],
            indent: { left: 360 },
          }),
        );
      });
      bodyParagraphs.push(new Paragraph({ children: [new TextRun("")] }));
    }
  }

  const doc = new Document({
    creator: ptaName,
    title: `Grant Application — ${grantName}`,
    sections: [
      {
        properties: {
          page: {
            size: { width: 11906, height: 16838 }, // A4
            margin: { top: 1134, right: 1134, bottom: 1134, left: 1134 },
          },
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({ text: `${ptaName}  •  Page `, color: "888888", size: 16 }),
                  new TextRun({ children: [PageNumber.CURRENT], color: "888888", size: 16 }),
                  new TextRun({ text: " of ", color: "888888", size: 16 }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES], color: "888888", size: 16 }),
                ],
              }),
            ],
          }),
        },
        children: bodyParagraphs,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Grant-Application-${funderSlug(funder)}-${new Date().toISOString().slice(0, 10)}.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
